// AppSync-Antworten ohne `data` und ohne `errors` (Proxy, Captive Portal,
// Firewall, Extension liefern 2xx mit Nicht-GraphQL-Body). Amplify reicht so
// eine Antwort als Erfolg durch, der Zugriff auf data[operation] wirft dann
// einen TypeError ohne jeden Hinweis auf die Ursache. Hier entsteht stattdessen
// ein Fehler mit Operation, Antwort-Vorschau und Client-Kontext, den
// applyBugsnagExtras in Bugsnag-Metadaten uebersetzt.

import { GRAPHQL_ERROR_FLAG } from './graphql-grouping'

const PREVIEW_LIMIT = 500
const BURST_WINDOW_MS = 60 * 1000

export const EMPTY_RESPONSE_REASON = 'empty response (no data)'

let emptyResponseTimestamps = []

export const hasGraphqlData = (response) => response?.data != null

export const registerEmptyResponse = (now = Date.now()) => {
  emptyResponseTimestamps = emptyResponseTimestamps.filter(
    (timestamp) => now - timestamp < BURST_WINDOW_MS
  )
  emptyResponseTimestamps.push(now)
}

export const getRecentEmptyResponseCount = (now = Date.now()) =>
  emptyResponseTimestamps.filter(
    (timestamp) => now - timestamp < BURST_WINDOW_MS
  ).length

export const resetEmptyResponseCounter = () => {
  emptyResponseTimestamps = []
}

const getPreview = (response) => {
  if (typeof response === 'string') return response.slice(0, PREVIEW_LIMIT)

  try {
    return (JSON.stringify(response) ?? String(response)).slice(
      0,
      PREVIEW_LIMIT
    )
  } catch {
    return Object.prototype.toString.call(response)
  }
}

const getType = (value) => {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

export const describeResponse = (response) => ({
  type: getType(response),
  keys:
    response && typeof response === 'object'
      ? Object.keys(response).slice(0, 20)
      : undefined,
  data_type: getType(response?.data),
  length: typeof response === 'string' ? response.length : undefined,
  preview: getPreview(response)
})

export const getClientContext = () => {
  const connection =
    typeof navigator !== 'undefined' ? navigator.connection : undefined

  return {
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    visibility:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
    connection_type: connection?.effectiveType,
    rtt_ms: connection?.rtt,
    downlink_mbps: connection?.downlink,
    save_data: connection?.saveData,
    page_age_s:
      typeof performance !== 'undefined'
        ? Math.round(performance.now() / 1000)
        : undefined
  }
}

/**
 * attempts: [{ response, duration_ms }], bei Retry mit beiden Versuchen.
 * outcome: 'not_retried' | 'retry_failed' | 'recovered'
 */
export const createEmptyResponseError = ({
  kind,
  operation,
  variables,
  input,
  authMode,
  hasNextToken = false,
  outcome,
  attempts
}) => {
  const additionalInfo =
    (variables && `variables: ${JSON.stringify(variables)}`) ||
    (input && `input: ${JSON.stringify(input)}`)

  const error = new Error(
    [operation, EMPTY_RESPONSE_REASON, additionalInfo, GRAPHQL_ERROR_FLAG]
      .filter((item) => item)
      .join(' / ')
  )

  error.bugsnag = {
    // Per Retry geheilte Aussetzer bleiben sichtbar, sind aber kein Fehler.
    severity: outcome === 'recovered' ? 'info' : 'error',
    metadata: {
      graphql_response: {
        kind,
        operation,
        auth_mode: authMode,
        has_next_token: hasNextToken,
        outcome,
        attempts: attempts.map(({ response, duration_ms }) => ({
          duration_ms,
          ...describeResponse(response)
        })),
        empty_responses_last_60s: getRecentEmptyResponseCount()
      },
      client: getClientContext()
    }
  }

  return error
}

// Wird im Bugsnag onError aufgerufen. Fehler koennen ueber `error.bugsnag`
// Severity und Metadaten mitgeben, ohne dass Bugsnag.notify direkt gerufen
// werden muss (console.error ist auf Bugsnag.notify(error) umgebogen).
export const applyBugsnagExtras = (event) => {
  const extras = event.originalError?.bugsnag
  if (!extras) return

  if (extras.severity) event.severity = extras.severity

  Object.entries(extras.metadata ?? {}).forEach(([tab, values]) => {
    event.addMetadata(tab, values)
  })
}
